// @flow
/** @jsx jsx */

import * as React from 'react';

import Icon from '@zapier/common-components/src/components/Icon';
import PopoverArrow from '@zapier/common-components/src/components/Popover/PopoverArrow';
import { Colors, Duration } from '@zapier/theme';
import { css, jsx, keyframes } from '@emotion/core';
import { getRef, setRef } from '@zapier/common-utils';

const EDGE_BUFFER = 5;

export type PopoverThemeType = 'default' | 'dark' | 'light' | 'teams';

type Position = {
  arrowLeft: number,
  arrowTop: number,
  contentLeft: number,
  contentTop: number,
  isArrowUp: boolean,
};

type Props = {
  alwaysShowAbove?: boolean,
  /** Left point to attach Node */
  attachLeft: number,
  /** Top poin to attach Node */
  attachTop: number,
  children: React.Node,
  /** Fade in popover when opened */
  fadeIn?: boolean,
  /* is attached */
  isAttached?: boolean,
  /** Show as compact popover */
  isCompact?: boolean,
  /** Maximum width */
  maxWidth?: string,
  /** Do not wrap text in popover */
  noWrap?: boolean,
  /** Called on Popover close */
  onClose?: () => null,
  /** Called when mouse enters Popover */
  onMouseEnter?: () => null,
  /** Called when mouse leaves Popover */
  onMouseLeave?: () => null,
  /** Render bounds */
  targetRect: {
    bottom: number,
    left: number,
    right: number,
    top: number,
  },
  /** Popover style theme */
  theme?: PopoverThemeType,
  /** Height of DOM window (from watchWindowSize) */
  windowHeight: number,
  /** Width of DOM window (from watchWindowSize) */
  windowWidth: number,
  /** Optional z-index. If present, the popover content receives a z-index of
   * the given value. The arrow receives a z-index of the given value plus 1.
   * Default: 1000.
   * */
  zIndex: number,
};

type State = {
  arrowDownHeight: number,
  arrowDownWidth: number,
  arrowUpHeight: number,
  arrowUpWidth: number,
  contentHeight: number,
  contentWidth: number,
};

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const arrowCss = (base: Object, position: Position, isUp: boolean = false) =>
  css(base, {
    left: position.arrowLeft,
    top: position.arrowTop + (position.isArrowUp ? 1 : -1),
    visibility:
      (position.isArrowUp && isUp) || !(position.isArrowUp || isUp)
        ? 'visible'
        : 'hidden',
    zIndex: base.zIndex + 1,
  });

const baseCss = (
  isAttached: boolean,
  contentWidth: number,
  contentHeight: number,
  zIndex: number
): Object => ({
  position: isAttached ? 'absolute' : 'fixed',
  visibility: contentWidth < 1 && contentHeight < 1 ? 'hidden' : 'visible',
  zIndex,
});

const getColor = (theme: string): string => {
  switch (theme) {
    case 'dark':
      return 'rgba(0, 0, 0, 0.8)';
    case 'light':
      return Colors.white;
    case 'teams':
      return Colors.mystique;
    default:
      return Colors.heroine;
  }
};

const getBorderColor = (theme: string) => {
  switch (theme) {
    case 'light':
      return Colors.adamantium;
    default:
      return null;
  }
};

const childrenCss = (theme?: string) => {
  return {
    color: theme === 'light' ? Colors.batmobile : Colors.white,
    flex: 1,
    lineHeight: 1.5,
    maxWidth: '100%',
    wordBreak: 'break-word',

    p: {
      margin: 0,
    },
  };
};

const closeButtonCss = {
  color: Colors.white,
  flex: 'none',
  height: '12px',
  margin: '0.2em 0 0 0.5em',
  width: '12px',
};

const contentCss = (
  base: Object,
  contentHeight: number,
  contentWidth: number,
  position
) =>
  css(
    {
      left: position.contentLeft,
      top: position.contentTop,
    },
    base
  );

const contentCopyCss = {
  left: 0,
  position: 'fixed',
  top: 0,
  visibility: 'hidden',
  zIndex: -1,
};

const contentWrapperCss = (
  isCompact: ?boolean,
  maxWidth: ?string,
  noWrap: ?boolean,
  theme?: PopoverThemeType
) => {
  const compact = isCompact
    ? {
        fontSize: '13px',
        lineHeight: '19.5px',
        padding: '10px',
      }
    : {};
  const wrap = noWrap
    ? {
        whiteSpace: 'nowrap',
      }
    : {};
  const light =
    theme === 'light'
      ? {
          backgroundColor: Colors.white,
          color: Colors.batmobile,
          border: `1px solid ${Colors.adamantium}`,
          boxShadow: '-2px 2px 4px rgba(0, 0, 0, 0.05)',
        }
      : {};
  return css(
    {
      alignItems: 'flex-start',
      backgroundColor: 'currentColor',
      borderRadius: '4px',
      display: 'flex',
      maxWidth: maxWidth || '400px',
      minWidth: '100px',
      padding: '15px',
      textAlign: 'center',
    },
    compact,
    wrap,
    light
  );
};

const wrapperCss = (fadeIn, theme) =>
  css(
    {
      color: getColor(theme),
    },
    fadeIn && {
      animation: `${fadeInAnimation} ${Duration.long}`,
    }
  );

class PopoverInner extends React.PureComponent<Props, State> {
  static defaultProps = {
    fadeIn: false,
    isAttached: false,
    isCompact: false,
    noWrap: false,
    theme: 'default',
    zIndex: 1000,
  };

  state = {
    arrowDownHeight: 0,
    arrowDownWidth: 0,
    arrowUpHeight: 0,
    arrowUpWidth: 0,
    contentHeight: 0,
    contentWidth: 0,
  };

  isUpdatingFromMeasure: ?boolean = null;

  componentDidMount() {
    this.measure();
  }

  componentDidUpdate() {
    this.measure();
  }

  /**
   * Calculate optimal position for popover based on target position
   */
  calculatePosition = (): Position => {
    const {
      alwaysShowAbove,
      attachLeft,
      attachTop,
      isAttached,
      targetRect,
      windowHeight,
      windowWidth,
    } = this.props;
    const {
      arrowDownHeight,
      arrowDownWidth,
      arrowUpHeight,
      arrowUpWidth,
      contentHeight,
      contentWidth,
    } = this.state;

    const targetCenterX =
      targetRect.left + (targetRect.right - targetRect.left) / 2;
    const contentLeftFromCenter = targetCenterX - contentWidth / 2;
    const contentLeftSafeRight =
      contentLeftFromCenter + contentWidth > windowWidth + EDGE_BUFFER
        ? windowWidth - contentWidth - EDGE_BUFFER
        : contentLeftFromCenter;
    const contentLeft =
      contentLeftSafeRight > EDGE_BUFFER ? contentLeftSafeRight : EDGE_BUFFER;
    const contentTopFromBottom = targetRect.bottom + arrowUpHeight;

    let contentTop = contentTopFromBottom;
    let isArrowUp = true;
    if (
      contentTopFromBottom + contentHeight + arrowUpHeight >
        windowHeight - EDGE_BUFFER ||
      alwaysShowAbove
    ) {
      contentTop = targetRect.top - contentHeight - arrowDownHeight;
      isArrowUp = false;
    }

    const arrowTop = isArrowUp
      ? targetRect.bottom
      : targetRect.top - arrowDownHeight;
    const arrowWidth = isArrowUp ? arrowUpWidth : arrowDownWidth;

    const position = {
      arrowLeft: targetCenterX - arrowWidth / 2,
      arrowTop,
      contentLeft,
      contentTop,
      isArrowUp,
    };

    if (isAttached) {
      position.contentTop = attachTop + position.contentTop - targetRect.top;
      position.contentLeft =
        attachLeft + position.contentLeft - targetRect.left;
      position.arrowTop = attachTop + position.arrowTop - targetRect.top;
      position.arrowLeft = attachLeft + position.arrowLeft - targetRect.left;
    }
    return position;
  };

  measure = () => {
    // Prevent measuring because of an update caused by measuring. Because of
    // minor differences in measurement after moving the Popover, you can get
    // into an infinite loop if you don't use this lock.
    if (this.isUpdatingFromMeasure) {
      return;
    }
    // Measure all the pieces.
    const newState = ['content', 'arrowUp', 'arrowDown'].reduce(
      (stateObj, refKey) => {
        // Get the node for the piece.
        const node = getRef(this, refKey);
        if (node) {
          // We want fractions of pixels, so can't just ask for `offsetWidth`
          // and `offsetHeight`.
          const rect = node.getBoundingClientRect();
          const offsetWidth = rect.right - rect.left;
          const offsetHeight = rect.bottom - rect.top;
          // Compare size to size in state.
          if (
            this.state[`${refKey}Width`] !== offsetWidth ||
            this.state[`${refKey}Height`] !== offsetHeight
          ) {
            // If there's a change, start building a state object.
            stateObj = stateObj || {};
            stateObj[`${refKey}Width`] = offsetWidth;
            stateObj[`${refKey}Height`] = offsetHeight;
          }
        }
        return stateObj;
      },
      null
    );

    // If we have a new state object, it means something has changed sizes.
    if (newState) {
      // Lock against calling measure while updating the state.
      this.isUpdatingFromMeasure = true;
      this.setState(newState, () => {
        // Next update will get called before this.
        this.isUpdatingFromMeasure = false;
      });
    }
  };

  renderArrow = (
    baseStyle: {},
    position: Position,
    theme: string,
    isUp: boolean = false
  ) => (
    <div
      aria-hidden="true"
      css={arrowCss(baseStyle, position, isUp)}
      ref={setRef(this, isUp ? 'arrowUp' : 'arrowDown')}
    >
      <PopoverArrow
        borderColor={getBorderColor(theme)}
        direction={isUp ? 'up' : 'down'}
        fillColor={getColor(theme)}
      />
    </div>
  );

  renderContent = (
    { children, isCompact, maxWidth, noWrap, theme }: Props,
    refKey: ?string,
    baseStyle: {},
    onClose: ?() => null
  ) => {
    return (
      <div
        css={css(
          baseStyle,
          contentWrapperCss(isCompact, maxWidth, noWrap, theme)
        )}
        ref={refKey ? setRef(this, refKey) : null}
      >
        <div css={childrenCss(theme)}>{children}</div>
        {onClose && (
          <Icon
            css={closeButtonCss}
            name="delete"
            onClick={onClose}
            size={12}
          />
        )}
      </div>
    );
  };

  render() {
    const {
      fadeIn,
      isAttached,
      onClose,
      onMouseEnter,
      onMouseLeave,
      theme = 'default',
      zIndex,
    } = this.props;
    const { contentHeight, contentWidth } = this.state;
    const position = this.calculatePosition();
    const baseStyle = baseCss(
      Boolean(isAttached),
      contentWidth,
      contentHeight,
      zIndex
    );
    return (
      // This alert role is probably not accurate in all cases. We should use
      // aria-describedby in some cases, but the alert role at least makes it
      // known when there's an interaction, albeit possibly extra noisy in
      // some cases.
      <div
        css={wrapperCss(fadeIn, theme)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="alert"
      >
        {/* render copy of content for measuring at position 0, 0 */}
        {this.renderContent(this.props, 'content', contentCopyCss)}
        {/* render up and down arrows for measuring */}
        {this.renderArrow(baseStyle, position, theme, true)}
        {this.renderArrow(baseStyle, position, theme)}
        {/* render actual content, which we're forcing to match the copy */}
        {this.renderContent(
          this.props,
          null,
          contentCss(baseStyle, contentHeight, contentWidth, position),
          onClose
        )}
      </div>
    );
  }
}

export default PopoverInner;
